import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { eachDayOfInterval, endOfMonth, format, getDay, startOfMonth } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import {
  optionalInt,
  optionalOneOf,
  optionalString,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { orpc } from "@/lib/orpc";
import { useCopyAttendance } from "./-lib/use-copy-attendance";
import { AttendanceDialog } from "./-components/attendance-dialog";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { CopyAttendanceDialog } from "./-components/copy-attendance-dialog";
import { CopyBar } from "./-components/copy-bar";
import { RollGrid } from "./-components/roll-grid";
import { RollDayList } from "./-components/roll-day-list";
import { useIsMobile } from "@/hooks/use-mobile";
import { dayKey, sortPeople, type RollPerson, type SortOption } from "./-components/roll-model";
import { RollToolbar } from "./-components/roll-toolbar";
import { RollExports } from "./-components/roll-exports";

const SORTS = [
  "name-desc",
  "room-asc",
  "room-desc",
  "absences-most",
  "absences-fewest",
] as const satisfies readonly SortOption[];

type DailyRollSearch = {
  q?: string;
  status?: string;
  sort?: SortOption;
  year?: number;
  month?: number;
};

export const Route = createFileRoute("/_authenticated/_care/daily-roll/")({
  validateSearch: (search: Record<string, unknown>): DailyRollSearch => ({
    q: optionalSearch(search.q),
    status: optionalString(search.status)?.slice(0, 40) || undefined,
    sort: optionalOneOf(SORTS, search.sort),
    year: optionalInt(search.year, 2000, 2100),
    month: optionalInt(search.month, 0, 11),
  }),
  component: DailyRollPage,
});

function DailyRollPage() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const [today] = useState(() => new Date());
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<DailyRollSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const month = params.month ?? today.getMonth();
  const year = params.year ?? today.getFullYear();
  const status = params.status ?? "active";
  const sort = params.sort ?? "name-asc";
  const { text: query, change: setQuery } = useSearchParamText(params.q, (q) => patch({ q }));
  const setStatus = (next: string) => patch({ status: next === "active" ? undefined : next });
  const setSort = (next: SortOption) => patch({ sort: next === "name-asc" ? undefined : next });
  const setMonth = (next: number) => patch({ month: next === today.getMonth() ? undefined : next });
  const setYear = (next: number) =>
    patch({ year: next === today.getFullYear() ? undefined : next });
  const [marking, setMarking] = useState<{
    person: RollPerson;
    day: string;
    absent?: boolean;
  } | null>(null);
  const isMobile = useIsMobile();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const realFacility = facilityId === "all" ? "" : facilityId;

  const monthDates = useMemo(() => {
    const first = new Date(year, month, 1);
    return eachDayOfInterval({ start: startOfMonth(first), end: endOfMonth(first) });
  }, [month, year]);
  const days = monthDates.map((date) => ({
    key: dayKey(date),
    weekday: format(date, "EEE"),
    dayNumber: format(date, "d"),
    isWeekend: getDay(date) === 0 || getDay(date) === 6,
  }));

  const summary = useQuery({
    ...orpc.care.dailyRoll.summary.queryOptions({
      input: {
        facilityId: realFacility,
        startDate: days[0]?.key ?? "",
        endDate: days.at(-1)?.key ?? "",
        residentStatus: status,
      },
    }),
    enabled: Boolean(realFacility),
  });
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.care.dailyRoll.key() });
  const record = useMutation(
    orpc.care.dailyRoll.record.mutationOptions({
      onSuccess: async () => {
        toast.success("Attendance saved");
        setMarking(null);
        await refresh();
      },
      onError: () => toast.error("Couldn't save attendance. Check your connection and try again."),
    }),
  );
  const clearEntry = useMutation(
    orpc.care.dailyRoll.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Attendance cleared");
        setMarking(null);
        await refresh();
      },
      onError: () => toast.error("Couldn't clear attendance. Check your connection and try again."),
    }),
  );
  const people = summary.data ?? [];
  const shown = sortPeople(people, sort, query);
  const totalAbsences = shown.reduce((sum, person) => sum + person.absentDays, 0);
  const monthLabel = format(new Date(year, month, 1), "MMMM yyyy");
  const facilityName =
    facilities.data?.find((facility) => facility.id === realFacility)?.name ?? "Facility";
  const exportInput = { facilityId: realFacility, year, month: month + 1, residentStatus: status };

  const step = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    patch({
      month: next.getMonth() === today.getMonth() ? undefined : next.getMonth(),
      year: next.getFullYear() === today.getFullYear() ? undefined : next.getFullYear(),
    });
  };
  const clearSelection = () => {
    setSelected(new Set());
    setSourceKey(null);
  };

  const copy = useCopyAttendance({
    facilityId: realFacility,
    people,
    selected,
    sourceKey,
    onCopied: async () => {
      setCopying(false);
      clearSelection();
      await refresh();
    },
  });

  if (!realFacility)
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Daily roll" />
        <OneBuildingCard prompt="Choose a building. The daily roll is kept per building." />
      </div>
    );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Daily roll"
        meta={
          summary.isPending
            ? "Loading…"
            : summary.isError
              ? `${facilityName}, ${monthLabel}.`
              : `${facilityName}, ${monthLabel}. ${totalAbsences} absence${totalAbsences === 1 ? "" : "s"} recorded across ${shown.length} resident${shown.length === 1 ? "" : "s"}.`
        }
        actions={<RollExports input={exportInput} />}
      />
      <RollToolbar
        month={month}
        year={year}
        onMonth={setMonth}
        onYear={setYear}
        onStep={step}
        status={status}
        onStatus={setStatus}
        sort={sort}
        onSort={setSort}
        query={query}
        onQuery={setQuery}
        shown={shown.length}
        total={people.length}
      />
      {selected.size > 0 || sourceKey ? (
        <CopyBar
          count={selected.size}
          sourceKey={sourceKey}
          onClear={clearSelection}
          onCopy={() => setCopying(true)}
        />
      ) : null}
      <Card className="gap-0 py-0">
        {summary.isPending ? (
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-11 w-full" />
            ))}
          </div>
        ) : summary.isError ? (
          <ReadFailed
            className="p-6"
            message="Could not load the daily roll. Do not treat this as every resident being accounted for."
            error={summary.error}
            onRetry={() => void summary.refetch()}
            retrying={summary.isFetching}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            title="No residents to roll"
            description="Nothing matches the status filter and search for this month."
            action={
              query.trim() || status !== "all" ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuery("");
                    setStatus("all");
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : isMobile ? (
          <RollDayList
            facilityId={realFacility}
            days={days}
            people={shown}
            pending={record.isPending}
            reload={async () => (await summary.refetch()).data ?? []}
            onPresent={(list, day, onlyUnmarked) =>
              record.mutate({
                facilityId: realFacility,
                onlyUnmarked,
                records: list.map((person) => ({
                  residentId: person.id,
                  attendanceDate: day,
                  status: "present" as const,
                })),
              })
            }
            onAbsent={(person, day) => setMarking({ person, day, absent: true })}
            onOpen={(person, day) => setMarking({ person, day })}
          />
        ) : (
          <RollGrid
            days={days}
            people={shown}
            selected={selected}
            sourceKey={sourceKey}
            onToggle={(id) =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onToggleAll={() =>
              setSelected(
                shown.every((person) => selected.has(person.id))
                  ? new Set()
                  : new Set(shown.map((person) => person.id)),
              )
            }
            onSource={(key) => setSourceKey((current) => (current === key ? null : key))}
            onCell={(person, day) => setMarking({ person, day })}
          />
        )}
      </Card>

      {marking ? (
        <AttendanceDialog
          facilityId={realFacility}
          person={marking.person}
          day={marking.day}
          record={marking.person.attendance.find((row) => row.attendanceDate === marking.day)}
          pending={record.isPending || clearEntry.isPending}
          startAbsent={marking.absent}
          onClose={() => setMarking(null)}
          onClear={() => {
            const existing = marking.person.attendance.find(
              (row) => row.attendanceDate === marking.day,
            );
            if (existing) clearEntry.mutate({ attendanceId: existing.id });
          }}
          onSave={(values) =>
            record.mutate({
              facilityId: realFacility,
              records: [{ residentId: marking.person.id, attendanceDate: marking.day, ...values }],
            })
          }
        />
      ) : null}
      {copying && sourceKey ? (
        <CopyAttendanceDialog
          residentCount={selected.size}
          sourceKey={sourceKey}
          pending={copy.isPending}
          onCopy={(targets) => copy.mutate(targets)}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </div>
  );
}
